<template>
  <!-- Beneficiary Details Modal -->
  <div class="modal fade" id="beneficiaryDetailsModal" tabindex="-1" data-bs-backdrop="static" data-bs-keyboard="false">
    <div class="modal-dialog modal-lg modal-dialog-centered">
      <div class="modal-content">
        <!-- Modal Header -->
        <div class="modal-header bg-info text-white">
          <h5 class="modal-title">
            <i class="bx bx-user-circle me-2"></i>
            Beneficiary Details
          </h5>
          <button type="button" class="btn-close btn-close-white" data-bs-dismiss="modal"></button>
        </div>

        <!-- Modal Body -->
        <div class="modal-body">
          <div v-if="beneficiary" class="row">
            <!-- Photo and Basic Info Section -->
            <div class="col-md-4 text-center mb-4">
              <!-- Beneficiary Photo -->
              <div class="position-relative d-inline-block mb-3">
                <img v-if="beneficiaryHasPhoto"
                     :src="getBeneficiaryPhotoUrl()"
                     class="rounded-circle border border-3 border-info shadow"
                     style="width: 150px; height: 150px; object-fit: cover;"
                     :alt="fullName"
                     @error="onImageError">

                <div v-else
                     class="bg-soft-info rounded-circle d-flex align-items-center justify-content-center shadow"
                     style="width: 150px; height: 150px;">
                  <span class="text-info display-3">{{ initials }}</span>
                </div>

                <!-- Status Badge -->
                <span class="position-absolute bottom-0 end-0 badge rounded-pill"
                      :class="getStatusBadgeClass()">
                  {{ capitalizeFirst(beneficiary.status) }}
                </span>
              </div>

              <!-- Name and Relationship -->
              <h5 class="mb-1">{{ fullName }}</h5>
              <p class="text-muted mb-2">
                <span class="badge bg-info bg-opacity-10 text-info">
                  <i class="bx bx-link me-1"></i>
                  {{ capitalizeFirst(beneficiary.relationship) }}
                </span>
              </p>

              <!-- Age and Gender -->
              <div class="d-flex justify-content-center gap-3 text-muted">
                <span>
                  <i :class="beneficiary.gender === 'male' ? 'bx bx-male text-primary' : 'bx bx-female text-danger'"></i>
                  {{ capitalizeFirst(beneficiary.gender) }}
                </span>
                <span>
                  <i class="bx bx-calendar me-1"></i>
                  {{ age }} years old
                </span>
              </div>
            </div>

            <!-- Detailed Information Section -->
            <div class="col-md-8">
              <!-- Personal Information Card -->
              <div class="card border-0 shadow-sm mb-3">
                <div class="card-header bg-light">
                  <h6 class="mb-0">
                    <i class="bx bx-user me-2"></i>
                    Personal Information
                  </h6>
                </div>
                <div class="card-body">
                  <div class="row g-3">
                    <div class="col-sm-6">
                      <label class="text-muted small">First Name</label>
                      <p class="mb-0 fw-medium">{{ beneficiary.first_name }}</p>
                    </div>
                    <div class="col-sm-6">
                      <label class="text-muted small">Middle Name</label>
                      <p class="mb-0 fw-medium">{{ beneficiary.middle_name || 'N/A' }}</p>
                    </div>
                    <div class="col-sm-6">
                      <label class="text-muted small">Surname</label>
                      <p class="mb-0 fw-medium">{{ beneficiary.surname || 'N/A' }}</p>
                    </div>
                    <div class="col-sm-6">
                      <label class="text-muted small">Grandfather Name</label>
                      <p class="mb-0 fw-medium">{{ beneficiary.grandfather_name || 'N/A' }}</p>
                    </div>
                    <div class="col-sm-6">
                      <label class="text-muted small">Date of Birth</label>
                      <p class="mb-0 fw-medium">{{ formatDate(beneficiary.date_of_birth) }}</p>
                    </div>
                    <div class="col-sm-6">
                      <label class="text-muted small">ID Number</label>
                      <p class="mb-0 fw-medium">{{ beneficiary.id_number || 'Not Provided' }}</p>
                    </div>
                    <div class="col-sm-6" v-if="beneficiary.phone_number">
                      <label class="text-muted small">Phone Number</label>
                      <p class="mb-0 fw-medium">
                        <i class="bx bx-phone me-1"></i>
                        {{ beneficiary.phone_number }}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Medical Information Card -->
              <div class="card border-0 shadow-sm mb-3">
                <div class="card-header bg-light">
                  <h6 class="mb-0">
                    <i class="bx bx-health me-2"></i>
                    Medical Information
                  </h6>
                </div>
                <div class="card-body">
                  <div class="row g-3">
                    <div class="col-sm-6">
                      <label class="text-muted small">Blood Group</label>
                      <p class="mb-0 fw-medium">
                        <span v-if="beneficiary.blood_group" class="badge bg-danger">
                          <i class="bx bx-droplet me-1"></i>
                          {{ beneficiary.blood_group }}
                        </span>
                        <span v-else class="text-muted">Not Recorded</span>
                      </p>
                    </div>
                    <div class="col-sm-6">
                      <label class="text-muted small">Allergies</label>
                      <p class="mb-0 fw-medium">
                        <span v-if="beneficiary.allergies" class="text-warning">
                          <i class="bx bx-error-circle me-1"></i>
                          {{ beneficiary.allergies }}
                        </span>
                        <span v-else class="text-muted">None Recorded</span>
                      </p>
                    </div>
                    <div class="col-12">
                      <label class="text-muted small">Chronic Conditions</label>
                      <p class="mb-0 fw-medium">
                        <span v-if="beneficiary.chronic_conditions" class="text-danger">
                          <i class="bx bx-first-aid me-1"></i>
                          {{ beneficiary.chronic_conditions }}
                        </span>
                        <span v-else class="text-muted">None Recorded</span>
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Coverage Information -->
              <div class="card border-0 shadow-sm">
                <div class="card-header bg-light">
                  <h6 class="mb-0">
                    <i class="bx bx-shield-check me-2"></i>
                    Coverage Status
                  </h6>
                </div>
                <div class="card-body">
                  <div class="row g-3">
                    <div class="col-sm-6">
                      <label class="text-muted small">Status</label>
                      <p class="mb-0">
                        <span class="badge" :class="getStatusBadgeClass()">
                          <i :class="getStatusIcon()" class="me-1"></i>
                          {{ getStatusText() }}
                        </span>
                      </p>
                    </div>
                    <div class="col-sm-6">
                      <label class="text-muted small">Coverage Cost</label>
                      <p class="mb-0 fw-medium">
                        <span v-if="isFreeDependent" class="badge bg-success">
                          <i class="bx bx-check-circle me-1"></i>
                          Free Coverage
                        </span>
                        <span v-else class="text-primary fw-bold">
                          ETB {{ formatCurrency(dependentCost) }}/year
                        </span>
                      </p>
                    </div>
                    <div class="col-12" v-if="beneficiary.suspension_date">
                      <label class="text-muted small">Suspension Details</label>
                      <div class="alert alert-warning mb-0 py-2">
                        <i class="bx bx-pause-circle me-1"></i>
                        Suspended on {{ formatDate(beneficiary.suspension_date) }}
                        <span v-if="beneficiary.suspension_reason" class="d-block small mt-1">
                          Reason: {{ beneficiary.suspension_reason }}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Photo Upload History -->
          <div v-if="beneficiary && beneficiary.profile_photo" class="mt-4">
            <div class="card border-0 bg-light">
              <div class="card-body py-2">
                <div class="d-flex align-items-center justify-content-between">
                  <small class="text-muted">
                    <i class="bx bx-image me-1"></i>
                    Photo uploaded: {{ formatDate(beneficiary.photo_upload_date) || 'Unknown' }}
                  </small>
                  <button class="btn btn-sm btn-outline-primary" @click="viewPhoto">
                    <i class="bx bx-show me-1"></i>
                    View Photo
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Modal Footer -->
        <div class="modal-footer bg-light">
          <div class="d-flex justify-content-between w-100">
            <div class="d-flex gap-2">
              <button v-if="beneficiary && beneficiary.status === 'active'"
                      type="button"
                      class="btn btn-primary btn-sm"
                      @click="editBeneficiary">
                <i class="bx bx-edit me-1"></i>
                Edit Details
              </button>

              <button v-if="beneficiary && beneficiary.status === 'active'"
                      type="button"
                      class="btn btn-success btn-sm"
                      @click="updatePhoto">
                <i class="bx bx-camera me-1"></i>
                Update Photo
              </button>

              <button v-if="beneficiary && beneficiary.status === 'active'"
                      type="button"
                      class="btn btn-danger btn-sm"
                      @click="removeBeneficiary">
                <i class="bx bx-trash me-1"></i>
                Remove
              </button>
            </div>

            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">
              <i class="bx bx-x me-1"></i>
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
export default {
  name: 'BeneficiaryDetailsModal',
  props: {
    cbhiNumber: {
      type: String,
      required: true
    },
    memberInfo: {
      type: Object,
      default: () => ({})
    },
    selectedPlan: {
      type: Object,
      default: () => null
    },
    beneficiaryIndex: {
      type: Number,
      default: 0
    }
  },
  data() {
    return {
      modalInstance: null,
      beneficiary: null
    };
  },
  computed: {
    fullName() {
      if (!this.beneficiary) return '';
      return `${this.beneficiary.first_name} ${this.beneficiary.middle_name || ''} ${this.beneficiary.surname || ''}`.trim();
    },

    initials() {
      if (!this.beneficiary) return 'NA';
      return (this.beneficiary.first_name?.charAt(0) || '') +
          (this.beneficiary.surname?.charAt(0) || '');
    },

    age() {
      return this.calculateAge(this.beneficiary?.date_of_birth);
    },

    beneficiaryHasPhoto() {
      return this.beneficiary && this.beneficiary.profile_photo;
    },

    isFreeDependent() {
      if (!this.selectedPlan) return false;
      const maxFree = parseInt(this.selectedPlan.max_free_dependents) || 0;
      return this.beneficiaryIndex < maxFree;
    },

    dependentCost() {
      if (!this.selectedPlan) return 0;
      return this.selectedPlan.dependent_annual_amount || 0;
    }
  },
  mounted() {
    const modalElement = document.getElementById('beneficiaryDetailsModal');
    if (modalElement && typeof bootstrap !== 'undefined') {
      this.modalInstance = new bootstrap.Modal(modalElement);
    }
  },
  methods: {
    openModal(beneficiary) {
      this.beneficiary = { ...beneficiary };
      if (this.modalInstance) {
        this.modalInstance.show();
      }
    },

    closeModal() {
      if (this.modalInstance) {
        this.modalInstance.hide();
      }
    },

    getBeneficiaryPhotoUrl() {
      if (!this.beneficiary?.profile_photo) return '';

      if (this.beneficiary.profile_photo.startsWith('http')) {
        return this.beneficiary.profile_photo;
      }
      return `https://api.paysol.co.ke/cbhi/uploads/beneficiary-photos/${this.beneficiary.profile_photo}`;
    },

    onImageError(event) {
      event.target.style.display = 'none';
    },

    getStatusBadgeClass() {
      const statusMap = {
        'active': 'bg-success',
        'suspended': 'bg-warning',
        'deleted': 'bg-danger',
        'inactive': 'bg-secondary'
      };
      return statusMap[this.beneficiary?.status] || 'bg-secondary';
    },

    getStatusIcon() {
      const statusMap = {
        'active': 'bx bx-check-circle',
        'suspended': 'bx bx-pause-circle',
        'deleted': 'bx bx-x-circle',
        'inactive': 'bx bx-circle'
      };
      return statusMap[this.beneficiary?.status] || 'bx bx-circle';
    },

    getStatusText() {
      const statusMap = {
        'active': 'Active - Covered',
        'suspended': 'Suspended',
        'deleted': 'Deleted',
        'inactive': 'Inactive'
      };
      return statusMap[this.beneficiary?.status] || 'Unknown';
    },

    calculateAge(dateOfBirth) {
      if (!dateOfBirth) return 0;
      const dob = new Date(dateOfBirth);
      const today = new Date();
      let age = today.getFullYear() - dob.getFullYear();
      const monthDiff = today.getMonth() - dob.getMonth();
      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) {
        age--;
      }
      return age;
    },

    formatDate(date) {
      if (!date) return '';
      return new Date(date).toLocaleDateString('en-US', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    },

    formatCurrency(amount) {
      return new Intl.NumberFormat().format(amount || 0);
    },

    capitalizeFirst(str) {
      if (!str) return '';
      return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
    },

    viewPhoto() {
      this.$emit('view-photo', this.beneficiary);
      this.closeModal();
    },

    editBeneficiary() {
      this.$emit('edit-beneficiary', this.beneficiary);
      this.closeModal();
    },

    updatePhoto() {
      this.$emit('update-photo', this.beneficiary);
      this.closeModal();
    },

    removeBeneficiary() {
      this.$emit('remove-beneficiary', this.beneficiary);
      this.closeModal();
    }
  }
};
</script>

<style scoped>
.bg-soft-info {
  background-color: rgba(13, 202, 240, 0.1);
}

.card {
  transition: all 0.3s ease;
}

.card:hover {
  transform: translateY(-2px);
}

.badge {
  font-size: 0.75rem;
  padding: 0.35rem 0.65rem;
}

.modal-header.bg-info {
  background: linear-gradient(135deg, #0dcaf0 0%, #0aa2c0 100%) !important;
}

.btn {
  transition: all 0.3s ease;
}

.btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}
</style>